import React, { FC, Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { DamPageSkeleton } from 'src/components/pages/dam/damSkeleton';
import { backofficeUrl } from 'src/shared/helpers';
import { legacyRedirectsDamViewerQuery } from './__generated__/legacyRedirectsDamViewerQuery.graphql';

const redirect = (to: string) => () => <Navigate to={backofficeUrl(to)} replace />;

const paramRedirect = (buildTo: (params: Record<string, string | undefined>) => string) => () => {
    const params = useParams<Record<string, string>>();
    return <Navigate to={backofficeUrl(buildTo(params))} replace />;
};

// 3D Preview: /preview3d/* → /3Dpreview-and-AR/*
export const Preview3DProductsRedirect = redirect('/3Dpreview-and-AR/products');
export const Preview3DCamerasRedirect = redirect('/3Dpreview-and-AR/cameras');
export const Preview3DRedirect = paramRedirect(({ subpage }) => (subpage ? `/3Dpreview-and-AR/${subpage}` : '/3Dpreview-and-AR'));

// Test mode: /preview/test/* → /test-mode/*
export const TestModeRedirect = paramRedirect(({ productId }) => (productId ? `/test-mode/${productId}` : '/test-mode/'));

// Draft designs: /draftdesigns → /draft-designs, /draftconfigurations → /draft-configurations
export const DraftDesignsRedirect = redirect('/draft-designs');
export const DraftConfigurationsRedirect = redirect('/draft-configurations');

// AI tools: /ai/* → /ai-creative-tools/*
export const AIRedirect = redirect('/ai-creative-tools');
export const AIImageGenerationRedirect = redirect('/ai-creative-tools/image-generation-editing');
export const AIDesignGenerationRedirect = redirect('/ai-creative-tools/design-generation');
export const AIStudioLegacyRedirect = redirect('/ai-creative-tools/studio');
export const AIProductStagingRulesLegacyRedirect = redirect('/ai-creative-tools/lifestyle-scenes-rules');
export const AIProductStagingRuleLegacyRedirect = paramRedirect(({ id }) =>
    id ? `/ai-creative-tools/lifestyle-scenes-builder/${id}` : '/ai-creative-tools/lifestyle-scenes-builder'
);
export const AIVirtualModelRulesLegacyRedirect = redirect('/ai-creative-tools/ai-tryon-rules');
export const AIVirtualModelRuleLegacyRedirect = paramRedirect(({ id }) =>
    id ? `/ai-creative-tools/ai-tryon-builder/${id}` : '/ai-creative-tools/ai-tryon-builder'
);

// AI creative tools: intermediate rename /product-staging-* and /virtual-model-* → lifestyle-scenes / ai-tryon
export const AIProductStagingRulesRedirect = redirect('/ai-creative-tools/lifestyle-scenes-rules');
export const AIProductStagingRuleRedirect = paramRedirect(({ id }) =>
    id ? `/ai-creative-tools/lifestyle-scenes-builder/${id}` : '/ai-creative-tools/lifestyle-scenes-builder'
);
export const AIVirtualModelRulesRedirect = redirect('/ai-creative-tools/ai-tryon-rules');
export const AIVirtualModelRuleRedirect = paramRedirect(({ id }) => (id ? `/ai-creative-tools/ai-tryon-builder/${id}` : '/ai-creative-tools/ai-tryon-builder'));

// Pricing: /pricing-advanced/* → /pricing-rule/*, /pricing-rules/* → /pricing-rule/*, /pricings-advanced → /pricings-rules
export const PricingAdvancedRedirect = paramRedirect(({ subpage }) => (subpage ? `/pricing-rule/${subpage}` : '/pricing-rule'));
export const PricingAdvancedEmbeddedRedirect = paramRedirect(({ subpage }) => (subpage ? `/pricing-rule-embedded/${subpage}` : '/pricing-rule-embedded'));
export const PricingRulesRedirect = paramRedirect(({ subpage }) => (subpage ? `/pricing-rule/${subpage}` : '/pricing-rule'));
export const PricingRulesEmbeddedRedirect = paramRedirect(({ subpage }) => (subpage ? `/pricing-rule-embedded/${subpage}` : '/pricing-rule-embedded'));
export const PricingsAdvancedRedirect = redirect('/pricings-rules');

// Image gallery: /tools/images/* → /tools/image-gallery/*
export const ImagesRedirect = redirect('/tools/image-gallery');
export const ImagesSubpageRedirect = paramRedirect(({ subpage }) => `/tools/image-gallery/${subpage}`);

// Profanity filter: /tools/profanity-filter-rules → /tools/profanity-filter-and-image-moderator
export const ProfanityFilterRulesRedirect = redirect('/tools/profanity-filter-and-image-moderator');

// MOQ: /tools/moq-menu and /tools/moq-rules → /tools/moq
export const MoqMenuRedirect = redirect('/tools/moq');
export const MoqRulesRedirect = redirect('/tools/moq');

// Collage layout: /tools/collage-layout/* → /tools/collage-layouts/*
// Note: /tools/collage-layouts/rules (GridRules list) was also removed — redirect to main page
export const CollageLayoutRedirect = redirect('/tools/collage-layouts');
export const CollageLayoutGridRulesRedirect = redirect('/tools/collage-layouts');
export const CollageLayoutGridRuleRedirect = paramRedirect(({ id }) => (id ? `/tools/collage-layouts/rule/${id}` : '/tools/collage-layouts/rule'));
export const CollageLayoutTabRedirect = paramRedirect(({ tab }) => `/tools/collage-layouts/${tab}`);

// Bulk variation form: old /settings/* paths and /tools/bulk-variation/:modelId
export const BulkVariationSettingsListRedirect = redirect('/tools/bulk-variation-form/products');
export const BulkVariationSettingsRedirect = paramRedirect(({ modelId }) => `/tools/bulk-variation-form/${modelId}`);
export const BulkVariationProductListRedirect = redirect('/tools/bulk-variation-form/products');
export const BulkVariationRedirect = paramRedirect(({ modelId }) => `/tools/bulk-variation-form/${modelId}`);

// Template tasks: /tools/template-tasks/* → /tools/template-product-selection/*
export const TemplateTasksRedirect = redirect('/tools/template-product-selection');
export const TemplateTaskDetailRedirect = paramRedirect(({ taskId }) => `/tools/template-product-selection/tasks/${taskId}`);

// Advanced print file: /tools/placement-product-selection and /tools/placements/:modelId
export const PlacementProductSelectionRedirect = redirect('/tools/advanced-print-file/products');
export const PlacementsRedirect = paramRedirect(({ modelId }) => `/tools/advanced-print-file/${modelId}`);

// Fill background: /tools/area-background/* → /tools/fill-background/*
export const AreaBackgroundRedirect = redirect('/tools/fill-background');
export const AreaBackgroundRulesRedirect = redirect('/tools/fill-background');
export const AreaBackgroundRuleRedirect = paramRedirect(({ id }) => (id ? `/tools/fill-background/rule/${id}` : '/tools/fill-background/rule'));

// Translations: /settings/translations-container/:page → /settings/translations
export const TranslationsContainerRedirect = redirect('/settings/translations');

// Customizer messages: /settings/customizer-messages/ → /settings/messages-and-alerts/
export const CustomizerMessagesRedirect = redirect('/settings/messages-and-alerts/');

// Integration settings: /settings/sales-channel-integration/* → /settings/integration-settings/*
export const SalesChannelIntegrationRedirect = redirect('/settings/integration-settings');
export const SalesChannelIntegrationPlatformRedirect = paramRedirect(({ platform }) =>
    platform ? `/settings/integration-settings/${platform}` : '/settings/integration-settings'
);

// Integrations page (removed) → integration settings
export const IntegrationsRedirect = redirect('/settings/integration-settings');

// Legacy platform settings pages (removed) → unified integration settings
export const LegacyPlatformSettingsRedirect = redirect('/settings/integration-settings');

// DAM: /dam?page=/gallery|/sku-list|/analytics|/tags (old iframe query-param scheme, still
// produced by MenuHelper.cs-generated sidebar links and the dashboard Solutions CTA) → the
// native /dam/asset-library|/dam/projects|/dam/analytics|/dam/tags pages.
const DAM_LEGACY_PAGE_MAP: Record<string, string> = {
    '/gallery': '/dam/asset-library',
    '/sku-list': '/dam/projects',
    '/analytics': '/dam/analytics',
    '/tags': '/dam/tags'
};

const DamLegacyRedirectContent: FC = () => {
    const [searchParams] = useSearchParams();
    const data = useLazyLoadQuery<legacyRedirectsDamViewerQuery>(
        graphql`
            query legacyRedirectsDamViewerQuery {
                subscription {
                    tools {
                        isViewerEnabled
                    }
                }
            }
        `,
        {}
    );

    if (!(data.subscription?.tools?.isViewerEnabled ?? false)) {
        return <Navigate to={backofficeUrl('/landings/viewer')} replace />;
    }

    const page = searchParams.get('page');
    const target = (page && DAM_LEGACY_PAGE_MAP[page]) || '/dam/asset-library';
    return <Navigate to={backofficeUrl(target)} replace />;
};

export const DamLegacyRedirect: FC = () => (
    <Suspense fallback={<DamPageSkeleton />}>
        <DamLegacyRedirectContent />
    </Suspense>
);
